/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo } from 'react'
import { Divider, Stack } from '@mui/material'

import { SeedMix, SeedMixSchema, SeedStoreInfo } from '../types/SeedOrderFormType'
import TestIds from '../../../testIds'
import { PaddockForm } from './PaddockForm'
import { SeedProductForm } from './SeedProductForm'
import { NotesForm } from './NotesForm'
import { useSeedOrderPickUpStoreModal } from '../components/PickUpStoreModal'
import Typography from '../../../components/Typography'
import { FulfillmentButton } from '../components/FulfillmentButton'
import { SeedMixMenu } from '../components/SeedMixMenu'
import SeedFormTitle from '../components/SeedFormTitle'
import MixTypeIcon from '../components/MixTypeIcon'
import SeedCartSlice, {
  removeSeedMix,
  selectSeedOrderSyncing,
  setSeedValue,
} from '../storage/seedCart.slice'
import { useSelector } from 'react-redux'
import { selectCurrentFarmerAccount } from '../../azure-b2c/storage'
import { StoreInfo } from '../../stores/storage/stores.types'
import _ from 'lodash'
import { useAppDispatch } from '../../../store/rootStore'

interface AddressChangeProps {
  addressName: string
  city: string
  streetAddress: string
  postCode: string
  address: string
  recordId?: number
  deliveryInstructions: string
  isNewAddress: boolean
}

const isSeedStoreInfo = (item: any): item is SeedStoreInfo => 'id' in item
const isStoreInfo = (item: any): item is StoreInfo =>
  'inventoryLocationId' in item && !('id' in item)
const isAddressChangeProps = (item: any): item is AddressChangeProps => 'streetAddress' in item

export const SeedMixForm = ({
  addCustomSeed,
  addPremixSeed,
  copySeed,
  seedMix,
  formInvalidated,
}: {
  addCustomSeed: () => void
  addPremixSeed: () => void
  copySeed: (seed?: SeedMix) => void
  seedMix: SeedMix
  formInvalidated?: boolean
}) => {
  const dispatch = useAppDispatch()
  const currentAccount = useSelector(selectCurrentFarmerAccount)

  const mixId = seedMix.id
  const storeId = seedMix.storeId

  const deliveryAddress = seedMix.deliveryAddress

  useEffect(() => {
    if (currentAccount?.accountNumber && mixId) {
      dispatch(
        SeedCartSlice.actions.setStoreIdAndDeliveryAddress({
          accountNumber: currentAccount.accountNumber,
          mixId,
          storeId,
          deliveryAddress,
        }),
      )
    }
  }, [storeId, deliveryAddress])

  const syncing = useSelector(selectSeedOrderSyncing)

  const validation = SeedMixSchema.safeParse(seedMix)
  const errors = validation.error?.formErrors.formErrors

  const { setOpen, setCallback } = useSeedOrderPickUpStoreModal<
    StoreInfo | SeedStoreInfo | AddressChangeProps
  >()
  const handleItemWithId = (item: SeedStoreInfo, seedMix: SeedMix) => {
    const accountNumber = currentAccount?.accountNumber
    if (accountNumber) {
      const copy = _.cloneDeep(seedMix)
      copy.storeId = item.id
      copy.deliveryAddress = null
      copy.deliveryInstructions = null
      dispatch(
        setSeedValue({
          accountNumber,
          key: '',
          value: copy,
          seedMix,
        }),
      )
    }
  }

  const handleItemWithInventoryLocationId = (item: StoreInfo, seedMix: SeedMix) => {
    const accountNumber = currentAccount?.accountNumber
    if (accountNumber) {
      const copy = _.cloneDeep(seedMix)
      copy.storeId = item.inventoryLocationId
      copy.deliveryAddress = null
      copy.deliveryInstructions = null
      dispatch(
        setSeedValue({
          accountNumber,
          key: '',
          value: copy,
          seedMix,
        }),
      )
    }
  }

  const handleItemWithStreetAddress = (item: AddressChangeProps, seedMix: SeedMix) => {
    const accountNumber = currentAccount?.accountNumber
    if (accountNumber) {
      const copy = _.cloneDeep(seedMix)
      copy.storeId = null
      copy.deliveryAddress = {
        name: item.addressName,
        streetAddress: item.streetAddress,
        city: item.city,
        postcode: item.postCode,
        recordId: item.recordId,
      }
      if (item.deliveryInstructions) {
        copy.deliveryInstructions = item.deliveryInstructions
      }
      dispatch(
        setSeedValue({
          accountNumber,
          key: '',
          value: copy,
          seedMix,
        }),
      )
    }
  }

  const handleItemWithDate = (item: any, seedMix: SeedMix) => {
    const accountNumber = currentAccount?.accountNumber
    if (accountNumber && item.date) {
      const copy = _.cloneDeep(seedMix)
      copy.paddocks = copy.paddocks.map((paddock) => {
        return {
          ...paddock,
          deliveryDate: item.date,
        }
      })
      dispatch(
        setSeedValue({
          accountNumber,
          key: '',
          value: copy,
          seedMix,
        }),
      )
    }
  }

  const openAddress = () => {
    setCallback(() => async (item: StoreInfo | SeedStoreInfo | AddressChangeProps) => {
      if (isSeedStoreInfo(item)) {
        handleItemWithId(item, seedMix)
      }
      if (isStoreInfo(item)) {
        handleItemWithInventoryLocationId(item, seedMix)
      }
      if (isAddressChangeProps(item)) {
        handleItemWithStreetAddress(item, seedMix)
      }
      handleItemWithDate(item, seedMix)
    })
    setOpen(true)
  }

  const name = seedMix.name
  const mixType = seedMix.mixType

  const onCopySeedMix = () => {
    copySeed(seedMix)
  }
  const onAddCustom = () => {
    addCustomSeed()
  }
  const onAddPremix = () => {
    addPremixSeed()
  }

  const onRemoveSeedMix = async () => {
    if (currentAccount?.accountNumber) {
      await dispatch(
        removeSeedMix({ accountNumber: currentAccount.accountNumber, seedMix: seedMix }),
      )
    }
  }

  const renderFulfillmentError = useMemo(() => {
    if (errors && errors.length > 0) {
      return (
        <Typography
          variant='body2'
          color='error700'
          mt={0.5}
          data-testid={TestIds.feat.seed.address.error}
        >
          {errors}
        </Typography>
      )
    }
    return null
  }, [errors])

  return (
    <Stack bgcolor='white' direction='column' className={syncing ? 'shimmer-container' : ''}>
      <SeedFormTitle
        title={name}
        icon={<MixTypeIcon type={mixType} />}
        kebabMenu={
          <SeedMixMenu
            seedMix={seedMix}
            onAddPremix={onAddPremix}
            onAddCustom={onAddCustom}
            onCopy={onCopySeedMix}
            onRemove={onRemoveSeedMix}
          />
        }
      >
        <FulfillmentButton seedMix={seedMix} openAddress={openAddress} sx={{ mt: 1 }} />
        {renderFulfillmentError}
      </SeedFormTitle>

      {(storeId || deliveryAddress) && (
        <>
          <Divider />
          <PaddockForm seedMix={seedMix} />
          <Divider />
          <SeedProductForm formInvalidated={formInvalidated} seedMix={seedMix} />
          <Divider />
          <NotesForm seedMix={seedMix} />
        </>
      )}
    </Stack>
  )
}
